import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { IUserContextType, UserContext } from '~utils/context/UserContext'
import { Button, Form, FormInput } from '@connectcore/connect-components-react'
import {
  EMAIL_PATTERN,
  INVALID_EMAIL_ERROR,
  JPMC_EMAIL_PATTERN,
  JPMC_EMAIL_ERROR,
  LOGIN_FAILURE_SCD_ERROR,
  USEREMAIL_REQUIRED_MESSAGE,
} from '~utils/constants'
import { ALIGN_BUTTONS_AND_LINKS } from '~utils/featureFlags'
import { CallbackType } from '@forgerock/javascript-sdk'

interface IFormData {
  formData: { userEmail: string | undefined }
  feedback: { userEmail: string | undefined }
}
const CollectEmailForm: FC = () => {
  const [data, setData] = useState({ email: null })
  const [apiError, setApiError] = useState(false)
  const { step, nextStep, showErr } = useContext(UserContext) as IUserContextType
  const [formState, setFormState] = useState<IFormData>({
    formData: { userEmail: undefined },
    feedback: { userEmail: undefined },
  })
  const [emailError, setEmailError] = useState('')
  let emailCallBackFn
  const forgotUsernameRef = useRef<any>(null)

  if (step?.getCallbacksOfType(CallbackType.NameCallback)?.length > 0)
    emailCallBackFn = step?.getCallbackOfType(CallbackType.NameCallback)

  const validateAndSetFormData = e => {
    setData({ ...data, ...e })
    setApiError(false)
    setEmailError('')
    if (Object.keys(e).includes('email') && !e?.email?.trim()) {
      const feedback = { userEmail: USEREMAIL_REQUIRED_MESSAGE }
      setFormState({ ...formState, feedback: { ...formState.feedback, ...feedback } })
    } else if (Object.keys(e).includes('email') && e.email) {
      setEmailError('')
      const feedback = { userEmail: undefined }
      setFormState({
        ...formState,
        feedback: { ...formState.feedback, ...feedback },
        formData: { ...formState.formData, ...e },
      })
    }
  }
  useEffect(() => {
    setApiError(showErr)
  }, [showErr])

  const isValidEmail = email => {
    return EMAIL_PATTERN.test(email)
  }
  const isJpmcEmail = email => {
    return JPMC_EMAIL_PATTERN.test(email)
  }

  const submitForm = () => {
    forgotUsernameRef.current.focus()
    if (data.email !== '' && isValidEmail(data.email) && !isJpmcEmail(data.email)) {
      emailCallBackFn?.setInputValue(data.email)
      nextStep(step)
    } else {
      setEmailError('')
      const feedback = { userEmail: USEREMAIL_REQUIRED_MESSAGE }
      setFormState({ ...formState, feedback: { ...formState.feedback, ...feedback } })
      if (isJpmcEmail(data.email)) {
        setEmailError(JPMC_EMAIL_ERROR)
        const feedback = { userEmail: undefined }
        setFormState({ ...formState, feedback: { ...formState.feedback, ...feedback } })
      } else if (!isValidEmail(data.email) && data.email) {
        setEmailError(INVALID_EMAIL_ERROR)
        const feedback = { userEmail: undefined }
        setFormState({ ...formState, feedback: { ...formState.feedback, ...feedback } })
      }
    }
  }

  return (
    <Form
      className="collect-email-form wide-form"
      data-testid="CollectEmailForm"
      onChange={e => {
        validateAndSetFormData(e)
      }}
      onSubmit={() => submitForm()}
      key="CollectEmailForm"
    >
      <div>
        <h2 className="fw-bold my-5 align-self-center">
          Forgot username - Enter your e-mail address
        </h2>
        <FormInput
          className="my-5 w-50"
          name="email"
          required={true}
          innerRef={forgotUsernameRef}
          label="User Email"
          feedback={<p role="alert">{formState.feedback.userEmail}</p>}
          validationState={
            formState.feedback.userEmail !== undefined && formState.feedback.userEmail?.length > 0
              ? 'invalid'
              : undefined
          }
        />
        <p className="invalid-feedback" role="alert">
          {emailError}
        </p>
      </div>
      {apiError && (
        <p className="invalid-feedback" role="alert">
          {LOGIN_FAILURE_SCD_ERROR}
        </p>
      )}
      <div className="d-flex flex-column">
        <Button kind="primary" className={`my-3 align-${ALIGN_BUTTONS_AND_LINKS}`}>
          Submit
        </Button>
      </div>
    </Form>
  )
}

export default CollectEmailForm
